import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import { newIdempotencyKey } from "@/lib/idempotency";
import {
  ApiClientError,
  TASK_FILTER_STATES,
  TASK_TERMINAL,
  type Project,
  type Task,
  type TaskEvent,
  type TaskFilterState,
  type TaskListPage,
} from "@/lib/types";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { Field, TextInput } from "@/components/Field";
import { ListTodo } from "@/components/icons";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { TaskResultView } from "@/components/TaskResult";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";
import styles from "./tasks.module.css";

const PAGE_LIMIT = 25;
const LIST_POLL_MS = 8000;
const DETAIL_POLL_MS = 4000;
const MAX_Q = 200;
const MAX_PAGE = 10000;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isFilterState(value: string | null): value is TaskFilterState {
  return Boolean(value && (TASK_FILTER_STATES as string[]).includes(value));
}

function readListQuery(params: URLSearchParams) {
  const q = (params.get("q") ?? "").trim().slice(0, MAX_Q);
  const stateRaw = params.get("state");
  const state = isFilterState(stateRaw) ? stateRaw : undefined;
  const projectRaw = params.get("project_id")?.trim() ?? "";
  const project_id = UUID_RE.test(projectRaw) ? projectRaw : undefined;
  const pageRaw = Number(params.get("page") || "1");
  let page = Number.isInteger(pageRaw) ? pageRaw : 1;
  if (page < 1) page = 1;
  if (page > MAX_PAGE) page = MAX_PAGE;
  return { q, state, project_id, page };
}

function hasActiveFilters(q: string, state?: TaskFilterState, projectId?: string): boolean {
  return Boolean(q || state || projectId);
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => resolve(), ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export function TasksPage() {
  const { m, locale, fmt } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = readListQuery(searchParams);
  const [qInput, setQInput] = useState(query.q);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectsRetry, setProjectsRetry] = useState(0);
  const [data, setData] = useState<TaskListPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshEpoch, setRefreshEpoch] = useState(0);
  const manualPendingRef = useRef(false);
  const queryKey = `${query.q}\0${query.state ?? ""}\0${query.project_id ?? ""}\0${query.page}`;

  useEffect(() => {
    setQInput(query.q);
  }, [query.q]);

  // Normalize invalid / oversized URL params back into the address bar.
  useEffect(() => {
    const rawQ = searchParams.get("q");
    const rawPage = searchParams.get("page");
    const rawState = searchParams.get("state");
    const rawProject = searchParams.get("project_id");
    let dirty = false;
    const next = new URLSearchParams();
    if (query.q) next.set("q", query.q);
    if (query.state) next.set("state", query.state);
    if (query.project_id) next.set("project_id", query.project_id);
    if (query.page > 1) next.set("page", String(query.page));
    if ((rawQ ?? "") !== (query.q || "") && (rawQ != null || query.q)) dirty = true;
    if (rawQ && rawQ.trim().length > MAX_Q) dirty = true;
    if (rawPage != null && String(query.page) !== rawPage && !(query.page === 1 && !rawPage)) {
      dirty = true;
    }
    if (rawState && !query.state) dirty = true;
    if (rawProject && !query.project_id) dirty = true;
    if (dirty && next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, query.q, query.state, query.project_id, query.page, setSearchParams]);

  useEffect(() => {
    let alive = true;
    setProjectsError(null);
    void api
      .projects()
      .then((res) => {
        if (!alive) return;
        setProjects(res.items);
        setProjectsError(null);
      })
      .catch((e) => {
        if (!alive) return;
        setProjects([]);
        setProjectsError(e instanceof Error ? e.message : m.tasks.projectsFailed);
      });
    return () => {
      alive = false;
    };
  }, [projectsRetry, m.tasks.projectsFailed]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const manual = manualPendingRef.current;
    manualPendingRef.current = false;

    setError(null);
    setRefreshError(null);
    if (manual) {
      setRefreshing(true);
    } else {
      setData(null);
      setLoading(true);
      setRefreshing(false);
    }

    function writePage(page: number) {
      const params = new URLSearchParams();
      if (query.q) params.set("q", query.q);
      if (query.state) params.set("state", query.state);
      if (query.project_id) params.set("project_id", query.project_id);
      if (page > 1) params.set("page", String(page));
      setSearchParams(params, { replace: true });
    }

    async function load(kind: "first" | "poll"): Promise<TaskListPage | null> {
      try {
        const res = await api.tasks(
          {
            q: query.q || undefined,
            state: query.state,
            project_id: query.project_id,
            page: query.page,
            limit: PAGE_LIMIT,
          },
          ac.signal,
        );
        if (!alive) return null;
        setData(res);
        setError(null);
        setRefreshError(null);

        const maxPage = Math.max(1, Math.ceil(res.total / PAGE_LIMIT));
        if (res.page > maxPage) {
          writePage(maxPage);
          return res;
        }
        if (res.items.length === 0 && res.page > 1 && res.total === 0) {
          writePage(1);
        }
        return res;
      } catch (e) {
        if (!alive || ac.signal.aborted) return null;
        const message = e instanceof Error ? e.message : m.states.error;
        if (kind === "first") {
          setData(null);
          setError(message);
          setRefreshError(null);
        } else {
          setRefreshError(message);
        }
        return null;
      } finally {
        if (alive) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }

    void (async () => {
      await load("first");
      while (alive) {
        try {
          await sleep(LIST_POLL_MS, ac.signal);
        } catch {
          break;
        }
        if (!alive) break;
        await load("poll");
      }
    })();

    return () => {
      alive = false;
      ac.abort();
      setLoading(false);
      setRefreshing(false);
    };
  }, [queryKey, refreshEpoch, query.q, query.state, query.project_id, query.page, m.states.error, setSearchParams]);

  function writeParams(next: {
    q?: string;
    state?: string;
    project_id?: string;
    page?: number;
  }) {
    const params = new URLSearchParams();
    const q = (next.q ?? query.q).trim().slice(0, MAX_Q);
    const state = next.state === undefined ? query.state : next.state || undefined;
    const projectRaw =
      next.project_id === undefined ? query.project_id : next.project_id || undefined;
    const project_id = projectRaw && UUID_RE.test(projectRaw) ? projectRaw : undefined;
    let page = next.page ?? query.page;
    if (!Number.isInteger(page) || page < 1) page = 1;
    if (page > MAX_PAGE) page = MAX_PAGE;
    if (q) params.set("q", q);
    if (state && isFilterState(state)) params.set("state", state);
    if (project_id) params.set("project_id", project_id);
    if (page > 1) params.set("page", String(page));
    setSearchParams(params, { replace: true });
  }

  function onSearchSubmit(e: FormEvent) {
    e.preventDefault();
    writeParams({ q: qInput, page: 1 });
  }

  function onManualRefresh() {
    manualPendingRef.current = true;
    setRefreshEpoch((n) => n + 1);
  }

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const filtered = hasActiveFilters(query.q, query.state, query.project_id);
  const items = data?.items ?? [];
  const showList = Boolean(data) && !error;

  return (
    <div>
      <h1 className={shell.pageTitle}>{m.tasks.title}</h1>
      <p className={shell.pageLead}>{m.tasks.lead}</p>

      <div className={styles.toolbar}>
        <form className={styles.filters} noValidate onSubmit={onSearchSubmit}>
          <Field label={m.tasks.searchLabel} htmlFor="tasks-q">
            <TextInput
              id="tasks-q"
              value={qInput}
              onChange={(e) => setQInput(e.target.value.slice(0, MAX_Q))}
              autoComplete="off"
              maxLength={MAX_Q}
              placeholder={m.tasks.searchPlaceholder}
            />
          </Field>
          <Field label={m.tasks.stateLabel} htmlFor="tasks-state">
            <select
              id="tasks-state"
              className={ui.input}
              value={query.state ?? ""}
              onChange={(e) => writeParams({ state: e.target.value, page: 1 })}
            >
              <option value="">{m.tasks.stateAll}</option>
              {TASK_FILTER_STATES.map((state) => (
                <option key={state} value={state}>
                  {lifecycleLabel(m, state)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={m.tasks.projectLabel} htmlFor="tasks-project">
            <select
              id="tasks-project"
              className={ui.input}
              value={query.project_id ?? ""}
              onChange={(e) => writeParams({ project_id: e.target.value, page: 1 })}
              disabled={Boolean(projectsError)}
            >
              <option value="">{m.tasks.projectAll}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <div className={styles.actionsRow}>
            <Button type="submit" variant="secondary">
              {m.tasks.applyFilters}
            </Button>
            <Button type="button" variant="ghost" disabled={refreshing || loading} onClick={onManualRefresh}>
              {refreshing ? m.tasks.refreshing : m.tasks.refresh}
            </Button>
          </div>
        </form>
        <div className={styles.actionsRow}>
          <ButtonLink to="/app/projects" variant="primary">
            {m.tasks.createViaProjects}
          </ButtonLink>
        </div>
      </div>

      {projectsError ? (
        <div className={styles.refreshError} role="alert">
          <span>
            {m.tasks.projectsFailed}: {projectsError}
          </span>
          <Button type="button" variant="secondary" onClick={() => setProjectsRetry((n) => n + 1)}>
            {m.tasks.retryRefresh}
          </Button>
        </div>
      ) : null}

      {error ? (
        <div className={styles.refreshError} role="alert">
          <span>
            {m.tasks.loadFailed}: {error}
          </span>
          <Button type="button" variant="secondary" onClick={onManualRefresh}>
            {m.tasks.retryRefresh}
          </Button>
        </div>
      ) : null}

      {refreshError && showList ? (
        <div className={styles.refreshError} role="status">
          <span>
            {m.tasks.refreshStale}: {refreshError}
          </span>
          <Button type="button" variant="secondary" onClick={onManualRefresh}>
            {m.tasks.retryRefresh}
          </Button>
        </div>
      ) : null}

      {loading && !data ? (
        <div aria-busy="true" aria-live="polite">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className={ui.skeletonRow} />
          ))}
        </div>
      ) : null}

      {showList && items.length === 0 ? (
        filtered ? (
          <div>
            <StatePanel kind="empty" title={m.tasks.noResults} message={m.tasks.noResultsBody} />
            {query.page > 1 ? (
              <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
                <Button type="button" variant="secondary" onClick={() => writeParams({ page: 1 })}>
                  {m.tasks.backToFirstPage}
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div>
            <StatePanel kind="empty" title={m.tasks.empty} message={m.tasks.emptyBody} />
            <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
              <ButtonLink to="/app/projects" variant="secondary">
                {m.tasks.createViaProjects}
              </ButtonLink>
            </div>
          </div>
        )
      ) : null}

      {showList && items.length > 0 ? (
        <div>
          {items.map((task) => (
            <Link key={task.id} to={`/app/tasks/${task.id}`} className={ui.row}>
              <StatusRail
                state={task.state}
                pulse={task.state === "running" || task.state === "queued"}
              />
              <span className={ui.rowMain}>
                <span className={ui.rowIcon}>
                  <ListTodo size={18} aria-hidden />
                </span>
                <span>
                  <strong>{task.title}</strong>
                  <div className={ui.meta}>
                    {lifecycleLabel(m, task.state)} · {lifecycleLabel(m, task.kind)} ·{" "}
                    {formatWhen(task.updated_at, locale)}
                  </div>
                </span>
              </span>
            </Link>
          ))}
          <div className={styles.pager}>
            <p className={styles.pagerMeta}>
              {fmt(m.tasks.pageStatus, {
                page: query.page,
                pages: pageCount,
                total,
              })}
            </p>
            <div className={styles.actionsRow}>
              <Button
                type="button"
                variant="secondary"
                disabled={query.page <= 1}
                onClick={() => writeParams({ page: query.page - 1 })}
              >
                {m.tasks.prevPage}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={query.page >= pageCount}
                onClick={() => writeParams({ page: query.page + 1 })}
              >
                {m.tasks.nextPage}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function TaskDetailPage() {
  const { taskId = "" } = useParams();
  const { m, locale } = useLocale();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [events, setEvents] = useState<TaskEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refreshEpoch, setRefreshEpoch] = useState(0);
  const cancelKeyRef = useRef<{ taskId: string; key: string } | null>(null);
  const manualPendingRef = useRef(false);
  const errorMessageRef = useRef(m.states.error);
  const refreshFailedMessageRef = useRef(m.tasks.refreshFailed);
  errorMessageRef.current = m.states.error;
  refreshFailedMessageRef.current = m.tasks.refreshFailed;

  useEffect(() => {
    setTask(null);
    setEvents([]);
    setError(null);
    setRefreshError(null);
    setActionError(null);
    setLoading(true);
    if (!cancelKeyRef.current || cancelKeyRef.current.taskId !== taskId) {
      cancelKeyRef.current = { taskId, key: newIdempotencyKey() };
    }
  }, [taskId]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const capturedTaskId = taskId;
    const manual = manualPendingRef.current;
    manualPendingRef.current = false;

    async function load(kind: "first" | "poll"): Promise<Task | null> {
      try {
        const [nextTask, nextEvents] = await Promise.all([
          api.task(capturedTaskId, ac.signal),
          api.taskEvents(capturedTaskId, ac.signal),
        ]);
        if (!alive) return null;
        if (nextTask.id !== capturedTaskId) return null;
        setTask(nextTask);
        setEvents(nextEvents.items);
        setError(null);
        setRefreshError(null);
        return nextTask;
      } catch (e) {
        if (!alive || ac.signal.aborted) return null;
        const err =
          e instanceof ApiClientError
            ? e
            : new ApiClientError(0, "error", errorMessageRef.current);
        // Manual/poll failures keep the current task; only a cold first load clears it.
        if (kind === "first" && !manual) {
          setTask(null);
          setEvents([]);
          setError(err);
          setRefreshError(null);
        } else {
          setRefreshError(err.message || refreshFailedMessageRef.current);
        }
        return null;
      } finally {
        if (alive) setLoading(false);
      }
    }

    void (async () => {
      let current = await load("first");
      while (alive) {
        if (current && TASK_TERMINAL.has(current.state)) break;
        try {
          await sleep(DETAIL_POLL_MS, ac.signal);
        } catch {
          break;
        }
        if (!alive) break;
        const next = await load("poll");
        if (next) current = next;
      }
    })();

    return () => {
      alive = false;
      ac.abort();
      setLoading(false);
    };
  }, [taskId, refreshEpoch]);

  function onManualRefresh() {
    manualPendingRef.current = true;
    setRefreshEpoch((n) => n + 1);
  }

  async function onCancel() {
    if (!task || TASK_TERMINAL.has(task.state) || task.state === "cancel_requested") return;
    const key =
      cancelKeyRef.current?.taskId === taskId
        ? cancelKeyRef.current.key
        : newIdempotencyKey();
    if (!cancelKeyRef.current || cancelKeyRef.current.taskId !== taskId) {
      cancelKeyRef.current = { taskId, key };
    }
    setBusy(true);
    setActionError(null);
    try {
      const op = await api.cancelTask(taskId, cancelKeyRef.current.key);
      navigate(`/app/operations/${op.id}`);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : m.tasks.cancelFailed);
    } finally {
      setBusy(false);
    }
  }

  const canCancel =
    Boolean(task) &&
    !TASK_TERMINAL.has(task!.state) &&
    task!.state !== "cancel_requested" &&
    !busy;

  return (
    <AuthStatePanels
      loading={loading && !task}
      error={error && !task ? error : null}
      empty={!task && !loading && !error ? <StatePanel kind="empty" title={m.tasks.notFound} /> : null}
    >
      {task && task.id === taskId ? (
        <div>
          <p className={ui.meta}>
            <Link to="/app/tasks">← {m.tasks.back}</Link>
            {" · "}
            <Link to={`/app/projects/${task.project_id}`}>{m.tasks.openProject}</Link>
          </p>
          <h1 className={shell.pageTitle}>{task.title}</h1>
          <p className={shell.pageLead}>
            {lifecycleLabel(m, task.state)} · {lifecycleLabel(m, task.kind)}
          </p>
          <p className={`mono ${ui.meta}`}>id {task.id}</p>

          {refreshError ? (
            <div className={styles.refreshError} role="status">
              <span>
                {m.tasks.refreshStale}: {refreshError}
              </span>
              <Button type="button" variant="secondary" onClick={onManualRefresh}>
                {m.tasks.retryRefresh}
              </Button>
            </div>
          ) : null}

          <div className={ui.panel} style={{ marginTop: "1rem" }}>
            <h2 className="display" style={{ fontSize: "1.05rem", marginTop: 0 }}>
              {m.tasks.instructions}
            </h2>
            <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{task.instructions}</p>
          </div>

          <div className={ui.panel} style={{ marginTop: "1rem" }}>
            <h2 className="display" style={{ fontSize: "1.05rem", marginTop: 0 }}>
              {m.tasks.timelineTitle}
            </h2>
            <p className={ui.meta} style={{ marginTop: 0 }}>
              {m.tasks.timelineScope}
            </p>
            {events.length === 0 ? (
              <p className={ui.meta}>{m.tasks.timelineEmpty}</p>
            ) : (
              <ol className={styles.timeline}>
                {events.map((event) => (
                  <li key={event.id} className={styles.timelineItem}>
                    <span className={styles.timelineDot} aria-hidden />
                    <div>
                      <strong>{lifecycleLabel(m, event.state)}</strong>
                      <div className={`mono ${ui.meta}`}>{formatWhen(event.created_at, locale)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className={ui.panel} style={{ marginTop: "1rem" }}>
            <h2 className="display" style={{ fontSize: "1.05rem", marginTop: 0 }}>
              {m.tasks.result}
            </h2>
            {task.error ? <p className={ui.fieldError}>{task.error}</p> : null}
            <TaskResultView result={task.result} userInstructions={task.instructions} />
            <p className={ui.meta}>
              {formatWhen(task.created_at, locale)} · {formatWhen(task.updated_at, locale)}
            </p>
          </div>

          {!TASK_TERMINAL.has(task.state) ? (
            <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "1rem" }}>
              <Button variant="danger" disabled={!canCancel} onClick={() => void onCancel()}>
                {busy
                  ? m.tasks.cancelling
                  : task.state === "cancel_requested"
                    ? m.tasks.cancelRequested
                    : m.tasks.cancel}
              </Button>
            </div>
          ) : null}
          {actionError ? <p className={ui.fieldError}>{actionError}</p> : null}
        </div>
      ) : null}
    </AuthStatePanels>
  );
}
